import { Link } from "react-router-dom";
import { ArrowUpRight, Linkedin, Twitter, Mail, Phone, MapPin } from "lucide-react";

const serviceLinks = [
  { name: "Company Registration", path: "/services/private-limited-company" },
  { name: "GST Registration", path: "/services/gst-registration" },
  { name: "Trademark Registration", path: "/services/trademark-registration" },
  { name: "Annual Compliance", path: "/services/annual-filing" },
  { name: "Income Tax Filing", path: "/services/income-tax-filing" },
  { name: "Virtual CFO", path: "/services/virtual-cfo" },
];

const categoriesLinks = [
  { name: "Registrations", path: "/services#registrations" },
  { name: "Incorporations", path: "/services#incorporations" },
  { name: "Compliance", path: "/services#compliance" },
  { name: "Tax & GST", path: "/services#tax-gst" },
  { name: "Licenses", path: "/services#licenses" },
  { name: "Intellectual Property", path: "/services#intellectual-property" },
];

const companyLinks = [
  { name: "About Us", path: "/team" },
  { name: "Case Studies", path: "/case-studies" },
  { name: "Blog", path: "/blog" },
  { name: "Contact", path: "/contact" },
];

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer data-testid="footer" className="bg-[#050505] border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-8">
          {/* Brand Column */}
          <div className="md:col-span-4">
            <Link
              to="/"
              className="flex items-center gap-2 mb-6"
            >
              <img src="/logo.png" alt="Finease Logo" className="h-[40%] w-[60%]" />
            </Link>
            <p className="text-slate-400 text-base leading-relaxed max-w-sm mb-6">
              India's trusted platform for business registration, compliance, tax filing,
              and legal documentation. Expert assistance for startups and SMEs.
            </p>
            <div className="flex gap-4 mb-6">
              <a
                href="https://linkedin.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-linkedin"
                className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center text-slate-400 hover:text-[#00C896] hover:border-[#00C896]/50 transition-colors"
              >
                <Linkedin size={18} />
              </a>
              <a
                href="https://twitter.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-twitter"
                className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center text-slate-400 hover:text-[#00C896] hover:border-[#00C896]/50 transition-colors"
              >
                <Twitter size={18} />
              </a>
            </div>
            {/* Contact Info */}
            <div className="space-y-3 text-sm">
              <a href="mailto:info@fineaseconsulting.com" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors">
                <Mail size={14} />
                info@fineaseconsulting.com
              </a>
              <a href="tel:+919899462024" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors">
                <Phone size={14} />
                +91 9899462024
              </a>
              <div className="flex items-start gap-2 text-slate-400">
                <MapPin size={14} className="shrink-0 mt-0.5" />
                <span>B4 ,313 First Floor, Keshav Puram Delhi 110035</span>
              </div>
            </div>
          </div>

          {/* Popular Services */}
          <div className="md:col-span-2">
            <h4 className="text-xs font-mono tracking-wider uppercase text-[#00C896]/80 mb-6">
              Popular Services
            </h4>
            <ul className="space-y-3">
              {serviceLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-slate-400 hover:text-white transition-colors text-sm flex items-center gap-1 group"
                  >
                    {link.name}
                    <ArrowUpRight
                      size={12}
                      className="opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all"
                    />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Categories */}
          <div className="md:col-span-2">
            <h4 className="text-xs font-mono tracking-wider uppercase text-[#00C896]/80 mb-6">
              Categories
            </h4>
            <ul className="space-y-3">
              {categoriesLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-slate-400 hover:text-white transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Links */}
          <div className="md:col-span-2">
            <h4 className="text-xs font-mono tracking-wider uppercase text-[#00C896]/80 mb-6">
              Company
            </h4>
            <ul className="space-y-3">
              {companyLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-slate-400 hover:text-white transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* CTA Column */}
          <div className="md:col-span-2">
            <h4 className="text-xs font-mono tracking-wider uppercase text-[#00C896]/80 mb-6">
              Get Started
            </h4>
            <p className="text-sm text-slate-400 mb-4">
              Need help with your business? Our experts are ready to assist.
            </p>
            <Link
              to="/contact"
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              className="inline-block bg-[#00C896] text-black text-sm font-semibold px-5 py-2.5 rounded-full hover:bg-[#00E6AC] transition-colors"
            >
              Free Consultation
            </Link>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-16 pt-8 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-slate-500 text-sm">
            © {currentYear} Finease. All rights reserved.
          </p>
          <div className="flex flex-wrap gap-6 text-sm text-slate-500">
            <Link to="/privacy" className="hover:text-white transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms" className="hover:text-white transition-colors">
              Terms of Service
            </Link>
            <Link to="/refund" className="hover:text-white transition-colors">
              Refund Policy
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
